import Vue from "vue";
import App from "./App";
import store from "./store";

import { library } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { fab } from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

// Font Awesome icon names this app renders (PascalCase, no "fa" prefix —
// library.add below restores it). Components reference them by kebab name,
// e.g. icon="book-open".
const faIcons = [
  "AddressCard",
  "ArrowLeft",
  "Reply",
  "Bullhorn",
  "BookOpen",
  // FT-1066b: the Day-length row's mark (FT-1058c picked it; the curated
  // list never carried it, so the icon rendered as an empty comment).
  "HourglassHalf",
  // FT-1321: BookDead (the Undertaker-prefill row's stand-in) stood down —
  // the row wears the Undertaker's own token art now (FT-1322).
  "BroadcastTower",
  "Chair",
  "ChartBar",
  "Suitcase",
  "Check",
  "CheckSquare",
  "ChevronDown",
  // FT-1020: the build panel's Tower row mark (there is no clock in the
  // fork's own art set yet — same standing note as Bell/BellSlash below).
  "Clock",
  "CloudMoon",
  "Cog",
  // FT-1206: the Chat level row's mark (there is no speech mark in the fork's
  // own art set yet — the Bell/BellSlash standing note applies).
  "Comments",
  // FT-1211: the whisper DOORWAYS' mark — the seat vocabulary's Whisper row
  // (both schemes) and the click scheme's plate-side disc. The single spoken
  // message, where `Comments` above is the chat feature itself and the plane
  // below means a message SENT.
  "CommentDots",
  "Copy",
  // FT-1350: the Checklist-strictness row's mark — a checklist being held to
  // (there is no checklist glyph in the fork's own art set — the
  // Bell/BellSlash standing note applies).
  "ClipboardCheck",
  "Bell",
  // FT-880: the crossed bell is the refused-audio notice — a player who cannot
  // hear the call-back. (There is no bell in the fork's own art set yet; when
  // one is cut, both of these become an <img> the way the strip's marks are.)
  "BellSlash",
  "Dice",
  "DoorOpen",
  "Dragon",
  "ExchangeAlt",
  "ExclamationTriangle",
  // FT-1003: the night sheet's granted-grimoire Show control.
  "Eye",
  // FT-1350: the spectator-grimoire toggle's Hidden face (the session pill's
  // spectator list) — the eye, closed.
  "EyeSlash",
  "FeatherAlt",
  "Fire",
  "FileCode",
  "FileUpload",
  "FlagCheckered",
  // FT-1308: the Chronicle filter bar's funnel — the passive glyph fronting
  // the dimension-button cluster (there is no funnel in the fork's own art
  // set — the Bell/BellSlash standing note applies).
  "Filter",
  // FT-1301: the Chronicle filter bar's dev-ledger switch (test games are
  // lab fixtures; there is no flask in the fork's own art set — the
  // Bell/BellSlash standing note applies).
  "Flask",
  // FT-1313: the night checklist's per-row Skip — the pass-over mark beside
  // Send (there is no skip glyph in the fork's own art set — the
  // Bell/BellSlash standing note applies).
  "Forward",
  // FT-1315's Ghost (the Ghost vote row's stand-in) stood down with FT-1321 —
  // the row wears the seat's own cowl mark (ui-ghost-cowl.png) now.
  // FT-1209: the Control settings tab's three row marks (Setup panel /
  // Control scheme) and the tab's own leaf mark — the person-with-a-gear,
  // distinct from the Game settings tab's plain Cog one leaf over.
  "Font",
  "MousePointer",
  "UserCog",
  "HandPaper",
  // FT-1213: the Control settings tab's "Hover coins" row mark — a resting
  // hand for a resting pointer (the other five rows reuse marks already
  // in this list).
  "HandPointer",
  "HandPointRight",
  "Heartbeat",
  "Home",
  "Image",
  // FT-1241: the town's two optional locks — Key marks the ENTER password
  // (the room key players give at the door), Lock the OPEN password (the
  // guest host's seat lock). Intro's host panel + the door-side ask.
  "Key",
  "Link",
  "Lock",
  // FT-1326: the Game-settings Automations row's mark — the wand says "the
  // machine acts on its own", where the cog it wore before read as a
  // settings control (the setting menu's own leaf already wears a cog).
  "Magic",
  "Mask",
  "MinusCircle",
  // FT-1461 (L.O.T.S.): the seat's Shadow-team mark on the Reader's alignment
  // control (Light wears the Sun already in this list). CloudMoon is here but
  // reads as weather; the plain crescent is the team glyph.
  "Moon",
  // FT-1206: the whisper's SENT face — the flying mark, the toast, the
  // composer's send button. (FT-1211: the doorways that OPEN a message moved
  // to CommentDots above; the plane stays where it means "sent".)
  "PaperPlane",
  "Pen",
  "PeopleArrows",
  "Plus",
  "PlusCircle",
  "Question",
  "Random",
  "RedoAlt",
  "Scroll",
  "SignInAlt",
  "SearchMinus",
  "SearchPlus",
  "Skull",
  // FT-1348: the Game settings rail's General leaf — settings at large
  // (there is no sliders glyph in the fork's own art set — the
  // Bell/BellSlash standing note applies).
  "SlidersH",
  "Square",
  // FT-860: the night sheet's phase bar wears the moon art for night and this
  // for day (there is no sun in our own asset set).
  "Sun",
  "TheaterMasks",
  // FT-1003: the granted grimoire's keep-shown pin.
  "Thumbtack",
  "Times",
  "TimesCircle",
  "TrashAlt",
  // FT-1338: the Player settings Timer row's Digital icon — a readout
  // screen (there is no digital-display glyph in the fork's own art set —
  // the Bell/BellSlash standing note applies).
  "Tv",
  "Undo",
  "User",
  "UserEdit",
  "UserFriends",
  "UserSecret",
  "UserSlash",
  "Users",
  "VenusMars",
  "VolumeUp",
  "VolumeMute",
  "VoteYea",
  "Walking",
  "WindowMaximize",
  "WindowMinimize"
];

// No component in our own code renders a brand icon today; kept for parity
// with the registry's brand namespace.
const fabIcons = ["Github", "Discord"];

library.add(
  ...faIcons.map((i) => fas["fa" + i]),
  ...fabIcons.map((i) => fab["fa" + i]),
);

Vue.component("font-awesome-icon", FontAwesomeIcon);
Vue.config.productionTip = false;

// Golem fork: the blood-drip overlay scrollbar (v-blood-scroll on any
// scroll container replaces its native bar with the drop art).
import BloodScroll from "./golem/bloodScrollbar";
Vue.directive("blood-scroll", BloodScroll);

// Golem fork (FT-1200): ask the platform who this browser is — the cookie
// rides the same-origin /api proxy, so a main-site login is a login here.
// Best-effort and async; the app boots signed-out and lights up when the
// answer lands (session.account).
// FT-1202: the personal prefs watch that same fact — bound BEFORE the /me
// ask fires, so the boot answer is caught exactly like any later login and
// the account's saved prefs come down with it (golem/prefs' account sync).
// FT-1226: and ask which flags are on for this caller — `labs` decides
// whether the guide's doors render at all (session.labs; Menu + App gates).
import { initAccount, initFlags } from "./golem/account";
import { bindPrefsAccount } from "./golem/prefs";
// FT-1482: the Reader's colour — paints --reader-* onto the root at import
// (gold by default, or this browser's saved pick) before the app mounts.
import "./golem/readerColor";
bindPrefsAccount(store);
initAccount(store);
initFlags(store);

new Vue({
  render: (h) => h(App),
  store,
}).$mount("#app");
